'use client';

import Image from 'next/image';
import { useProductBrand } from './ProductBrandProvider';
import type { CSSProperties } from 'react';

export const PRODUCT_NAME = 'EngageIQ';
export const COMPANY_NAME = 'NeutralAiz';

export type EngageIQLogoVariant = 'horizontal' | 'stacked' | 'symbol';
export type EngageIQLogoTheme = 'light' | 'dark';

const logoAssets: Record<
  EngageIQLogoVariant,
  Record<EngageIQLogoTheme, { src: string; width: number; height: number }>
> = {
  horizontal: {
    light: {
      src: '/images/engageiq/engageiq-horizontal-light.png',
      width: 1964,
      height: 355,
    },
    dark: {
      src: '/images/engageiq/engageiq-horizontal-dark.png',
      width: 1964,
      height: 355,
    },
  },
  stacked: {
    light: {
      src: '/images/engageiq/engageiq-stacked-light.png',
      width: 1254,
      height: 1254,
    },
    dark: {
      src: '/images/engageiq/engageiq-stacked-dark.png',
      width: 1254,
      height: 1254,
    },
  },
  symbol: {
    light: {
      src: '/images/engageiq/engageiq-symbol-light.png',
      width: 1026,
      height: 681,
    },
    dark: {
      src: '/images/engageiq/engageiq-symbol-dark.png',
      width: 1026,
      height: 681,
    },
  },
};

interface EngageIQLogoProps {
  variant?: EngageIQLogoVariant;
  /** Select the logo designed for placement on this surface theme. */
  theme?: EngageIQLogoTheme;
  alt?: string;
  width?: number;
  height?: number;
  className?: string;
  style?: CSSProperties;
  priority?: boolean;
  sizes?: string;
}

export function EngageIQLogo({
  variant = 'horizontal',
  theme = 'dark',
  alt = PRODUCT_NAME,
  width,
  height,
  className,
  style,
  priority = false,
  sizes,
}: EngageIQLogoProps) {
  const brand = useProductBrand();
  if (brand.id === 'instructorone') {
    return (
      <span role="img" aria-label={brand.name} className={className}
        style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%',
          fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2,
          color: 'var(--color-brand)', ...style }}>
        {brand.name}
      </span>
    );
  }
  const asset = logoAssets[variant][theme];

  return (
    <Image
      src={asset.src}
      alt={alt}
      width={width ?? asset.width}
      height={height ?? asset.height}
      className={className}
      style={style}
      priority={priority}
      sizes={sizes}
    />
  );
}

interface PrimaryBrandLogoProps extends Omit<EngageIQLogoProps, 'alt'> {
  customSrc?: string | null;
  customAlt?: string | null;
}

/**
 * Preserves tenant branding when configured and otherwise presents the selected
 * product identity. InstructorOne remains visible alongside a tenant image.
 */
export function PrimaryBrandLogo({
  customSrc,
  customAlt,
  variant = 'horizontal',
  theme = 'dark',
  width,
  height,
  className,
  style,
  priority = false,
  sizes,
}: PrimaryBrandLogoProps) {
  const brand = useProductBrand();
  if (customSrc) {
    const tenantLogo = (
      <Image
        src={customSrc}
        alt={customAlt || 'Organization logo'}
        width={width ?? 1651}
        height={height ?? 449}
        className={className}
        style={style}
        priority={priority}
        sizes={sizes}
      />
    );
    return brand.id === 'instructorone' ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', maxWidth: '100%' }}>
        {tenantLogo}
        <EngageIQLogo variant={variant} theme={theme} />
      </span>
    ) : tenantLogo;
  }

  return (
    <EngageIQLogo
      variant={variant}
      theme={theme}
      width={width}
      height={height}
      className={className}
      style={style}
      priority={priority}
      sizes={sizes}
    />
  );
}

interface NeutralAizAttributionProps {
  className?: string;
  logoHeight?: number;
}

/** Secondary company attribution for low-prominence brand surfaces. */
export function NeutralAizAttribution({
  className = '',
  logoHeight = 12,
}: NeutralAizAttributionProps) {
  const brand = useProductBrand();
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] text-white/45 ${className}`}
      aria-label={`${brand.name} by ${COMPANY_NAME}`}
    >
      <span aria-hidden="true">by</span>
      <Image
        src="/images/neutralaiz-logo-white.png"
        alt={COMPANY_NAME}
        width={568}
        height={95}
        className="opacity-55"
        style={{ width: 'auto', height: `${logoHeight}px` }}
      />
    </span>
  );
}

interface ProductCompanyLockupProps {
  className?: string;
  productHeight?: number;
  companyHeight?: number;
}

/** Compact dual-brand treatment intended for footers and similar surfaces. */
export function ProductCompanyLockup({
  className = '',
  productHeight = 18,
  companyHeight = 10,
}: ProductCompanyLockupProps) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <EngageIQLogo
        theme="dark"
        width={1964}
        height={355}
        style={{ width: 'auto', height: `${productHeight}px` }}
      />
      <span className="h-4 w-px bg-white/15" aria-hidden="true" />
      <NeutralAizAttribution logoHeight={companyHeight} />
    </span>
  );
}
